import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
    ArrowRightOnRectangleIcon,
    ChevronDownIcon,
    Cog6ToothIcon,
    DocumentPlusIcon,
    EnvelopeIcon,
    LinkIcon,
    PencilSquareIcon,
    PhoneIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { WhiteLabelContentMode } from "generated/graphql";
import React, { ComponentType, useCallback } from "react";
import { useWhiteLabelContentMode } from "store/selectors/branding";
import { useFeature, usePreferences } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";

const buttonClasses =
    "twoverride inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-sm sm:rounded text-gray-50 bg-brand-600 " +
    "hover:bg-brand-700 " +
    "space-x-1 items-center justify-around" +
    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500";

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
}

interface ActionsMenuSectionProps {
    children: React.ReactNode;
}

export const ActionsMenuSection: React.FC<ActionsMenuSectionProps> = props => {
    const { children } = props;
    return <div className="py-1">{children}</div>;
};

interface ActionsMenuHeaderProps {
    children: React.ReactNode;
}

export const ActionsMenuHeader: React.FC<ActionsMenuHeaderProps> = props => {
    return (
        <div className="px-4 py-2 text-brand-600 text-sm font-bold">{props.children}</div>
    );
};

interface ActionsMenuActionProps {
    onClick: () => void;
    disabled?: boolean;
    menuHidden: boolean;
    Icon: ComponentType<React.ComponentProps<"svg">>;
    hoverColor?: string;
    children: React.ReactNode;
}

export const ActionsMenuAction: React.FC<ActionsMenuActionProps> = props => {
    const { children, onClick, disabled, menuHidden, Icon } = props;
    const actualOnClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            onClick();
        },
        [onClick],
    );
    if (menuHidden) {
        return (
            <MenuItem disabled>
                <span className="hidden" aria-hidden="true" />
            </MenuItem>
        );
    }
    if (disabled) {
        return (
            <MenuItem disabled>
                <div
                    className={classNames(
                        "text-gray-400 flex items-center px-4 py-2 text-sm",
                    )}>
                    <div>
                        <Icon className="mr-3 h-5 w-5 text-gray-300" aria-hidden="true" />
                    </div>
                    <div>{children}</div>
                </div>
            </MenuItem>
        );
    }
    return (
        <MenuItem>
            {({ focus }) => (
                <a
                    href="#"
                    onClick={actualOnClick}
                    className={classNames(
                        focus ? "bg-brand-100 text-brand-900" : "text-gray-700",
                        "twoverride group flex items-center px-4 py-2 text-sm",
                    )}>
                    <div>
                        <Icon
                            className="mr-3 h-5 w-5 text-gray-400 group-hover:text-brand-600"
                            aria-hidden="true"
                        />
                    </div>
                    <div>{children}</div>
                </a>
            )}
        </MenuItem>
    );
};

interface ActionsMenuProps {
    children: React.ReactNode;
}

export const ActionsMenu: React.FC<ActionsMenuProps> = props => {
    const { children } = props;

    return (
        <Menu as="div" className="relative inline-block text-left z-1300">
            <div>
                <MenuButton className={buttonClasses}>
                    <div className="md:hidden">
                        <Cog6ToothIcon className="h-5 w-5" />{" "}
                    </div>
                    <div className="max-md:hidden">Actions</div>
                    <div>
                        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                    </div>
                </MenuButton>
            </div>
            <MenuItems className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 divide-y divide-gray-100 focus:outline-none max-h-[80vh] overflow-y-auto">
                {children}
            </MenuItems>
        </Menu>
    );
};

export const SignWiseMenuAction: React.FC<{ onClick: () => void }> = props => {
    return (
        <ActionsMenuAction
            Icon={PencilSquareIcon}
            menuHidden={false}
            onClick={props.onClick}>
            Request signature
        </ActionsMenuAction>
    );
};

export const TransferClientMenuAction: React.FC<{ onClick: () => void }> = props => {
    const { settings } = useTenantSettings();
    const contentMode = useWhiteLabelContentMode();

    return (
        <ActionsMenuAction
            Icon={ArrowRightOnRectangleIcon}
            menuHidden={
                !settings.enablePublicProfile ||
                contentMode !== WhiteLabelContentMode.Full
            }
            onClick={props.onClick}>
            Transfer client
        </ActionsMenuAction>
    );
};

interface FaxMenuActionProps {
    onClick: () => void;
    disabled?: boolean;
}

export const FaxMenuAction: React.FC<FaxMenuActionProps> = props => {
    const faxEnabled = useFeature("fax");

    return (
        <ActionsMenuAction
            Icon={PhoneIcon}
            menuHidden={!faxEnabled}
            disabled={props.disabled}
            onClick={props.onClick}>
            Fax to community
        </ActionsMenuAction>
    );
};

interface ShareInputFormMenuActionProps {
    onClick: () => void;
    shareInputFormLinkEnabled: boolean;
}

export const ShareInputFormMenuAction: React.FC<
    ShareInputFormMenuActionProps
> = props => {
    const hostedIntakeFormExistingEnabled = useFeature("hostedIntakeFormExisting");

    return (
        <ActionsMenuAction
            Icon={LinkIcon}
            menuHidden={
                !props.shareInputFormLinkEnabled || !hostedIntakeFormExistingEnabled
            }
            onClick={props.onClick}>
            Share input form
        </ActionsMenuAction>
    );
};

interface PaperIntakeMenuActionProps {
    onClick: () => void;
    disabled?: boolean;
}

export const PaperIntakeMenuAction: React.FC<PaperIntakeMenuActionProps> = props => {
    const paperIntakeEnabled = useFeature("paperIntake");

    return (
        <ActionsMenuAction
            Icon={DocumentPlusIcon}
            menuHidden={!paperIntakeEnabled}
            disabled={props.disabled}
            onClick={props.onClick}>
            Upload Paper Intake Form
        </ActionsMenuAction>
    );
};

interface NotifyUnselectedCommunitiesMenuActionProps {
    onClick: () => void;
}

export const NotifyUnselectedCommunitiesMenuAction: React.FC<
    NotifyUnselectedCommunitiesMenuActionProps
> = props => {
    const preferences = usePreferences();

    return (
        <ActionsMenuAction
            Icon={EnvelopeIcon}
            menuHidden={!preferences.useBulkCommunityEmail}
            onClick={props.onClick}>
            Notify communities
        </ActionsMenuAction>
    );
};

interface CollaboratorsMenuActionProps {
    onClick: () => void;
}

export const CollaboratorsMenuAction: React.FC<CollaboratorsMenuActionProps> = props => {
    const clientCollaboratorsFeature = useFeature("clientCollaborators");
    const { settings } = useTenantSettings();

    return (
        <ActionsMenuAction
            Icon={UsersIcon}
            menuHidden={!clientCollaboratorsFeature || !settings.clientCollaborators}
            onClick={props.onClick}>
            Collaborators
        </ActionsMenuAction>
    );
};
